import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Card, CardContent, Skeleton, Stack, Typography } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'

import { ChartDescription } from '../ChartDescription/ChartDescription'
import { sectionLabelSx } from '../helpers/typographyStyles'

/**
 * One duration bucket of the distribution ("1 day" → 37 games).
 *
 * Fed by POST /blueprint-families/{id}/duration-distribution (GR-2916).
 *
 * A game is counted in EVERY bucket it ran an event in, so the buckets overlap and
 * their counts do not sum to the family's game count — over a third of games span
 * more than one. That is why there is no `share` here and why the bars below are
 * scaled against the largest bucket rather than against a total: any percentage
 * would invite reading these as parts of a whole.
 */
export interface DurationBucket {
  // Stable bucket key: 'd1' | 'd2_3' | 'd4_6' | 'd7_13' | 'd14_27' | 'd28_31' | 'd32plus'
  key: string
  label: string
  // Secondary qualifier, e.g. "flash bundle" / "1 week". Optional.
  sublabel?: string
  gamesCount: number
}

interface TypicalDurationsCardProps {
  // Undefined = no data (all buckets empty or fetch unresolved) → the card
  // renders nothing (unless `isLoading` shows the skeleton).
  buckets?: DurationBucket[]
  // Copy for the header hint, e.g. "how long the 88 tracked games run their Basic Bundles".
  hint?: string
  // Caption under the title. Defaults to the blueprint-family wording; the event-category
  // and event-type pages pass their own, since each names a different population.
  description?: string
  // Distribution fetch in flight with no data yet → render a bar-shaped skeleton.
  isLoading?: boolean
}

// The BE always ships 7 buckets, so the skeleton mirrors that row count.
const SKELETON_ROW_COUNT = 7

// Shared row grid for real bars and skeleton rows so loading doesn't shift layout.
const bucketRowSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '96px 1fr 88px', sm: '140px 1fr 96px' },
  alignItems: 'center',
  gap: 1.5,
} as const

const FILL_TRANSITION = 'width 0.3s ease-out'

/**
 * Fill portion of a duration-bucket track. Plays a quick "grow from 0"
 * animation on mount and whenever `pct` changes (window toggle, segment
 * filter, etc.): the bar renders at 0 width, then grows to the target one
 * frame later so the CSS transition has a 0-width starting frame to animate
 * from. Data changes remount this card (it drops to the loading skeleton
 * mid-refetch), so animating on mount is what makes the fill visible.
 */
const DurationFillBar: React.FC<{ pct: number; color: string }> = ({ pct, color }) => {
  const [width, setWidth] = useState(0)

  useEffect(() => {
    // Ensure a 0-width painted frame, then grow to the target next frame.
    setWidth(0)
    const raf = requestAnimationFrame(() => setWidth(pct))
    return () => cancelAnimationFrame(raf)
  }, [pct])

  // Nothing to show for an empty bucket (and nothing to animate).
  if (pct <= 0) return null

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        // Floor only the resting/target width so tiny non-zero shares stay
        // visible; the animated 0-width start frame must be a true 0%.
        width: `${width <= 0 ? 0 : Math.max(2, width)}%`,
        borderRadius: '0.5rem',
        backgroundColor: color,
        transition: FILL_TRANSITION,
      }}
    />
  )
}

/**
 * "Typical durations" card from the v14 mockup: a horizontal-bar histogram of
 * how long adopting games run this blueprint's events, from the GR-2916
 * duration-distribution endpoint. Hidden entirely when there is no data (and
 * nothing loading) so the page ships without a dead section.
 */
export const TypicalDurationsCard: React.FC<TypicalDurationsCardProps> = ({ buckets, hint, description, isLoading }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const maxGamesCount = Math.max(0, ...(buckets ?? []).map((bucket) => bucket.gamesCount))

  if (!buckets || buckets.length === 0) {
    if (!isLoading) return null
    return (
      <Card>
        <CardContent>
          <Typography sx={{ ...sectionLabelSx, mb: 2 }}>{t('market-analytics:durations_title')}</Typography>
          <Stack spacing={1.25} aria-busy="true">
            {Array.from({ length: SKELETON_ROW_COUNT }, (_, idx) => (
              <Box key={idx} sx={bucketRowSx}>
                {/* body2-sized lines so each loading row matches the resolved row height */}
                <Skeleton variant="text" width="70%" sx={{ fontSize: '0.875rem' }} />
                <Skeleton variant="rounded" height={12} sx={{ borderRadius: '0.5rem' }} />
                <Skeleton variant="text" width="80%" sx={{ fontSize: '0.875rem', justifySelf: 'end' }} />
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="baseline" spacing={1.5} flexWrap="wrap" rowGap={0.5}>
            <Typography sx={sectionLabelSx}>{t('market-analytics:durations_title')}</Typography>
            {hint ? (
              <Typography variant="caption" color="text.secondary">
                {hint}
              </Typography>
            ) : null}
          </Stack>
          {/* Same caption slot every analytics chart has under its title. */}
          <ChartDescription>{description ?? t('market-analytics:durations_description')}</ChartDescription>
        </Box>
        <Stack spacing={1.25}>
          {buckets.map((bucket) => {
            // Bars are scaled against the busiest bucket, so the widest one is always
            // full. This is a relative-magnitude comparison, not a share of a total —
            // the buckets overlap, so they have no total to take a share of.
            const pct = maxGamesCount > 0 ? Math.round((bucket.gamesCount / maxGamesCount) * 100) : 0
            return (
              <Box key={bucket.key} sx={bucketRowSx}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', lineHeight: 1.2 }} noWrap>
                    {bucket.label}
                  </Typography>
                  {bucket.sublabel ? (
                    <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ lineHeight: 1.2 }}>
                      {bucket.sublabel}
                    </Typography>
                  ) : null}
                </Box>
                <Box sx={{ position: 'relative', height: 12, borderRadius: '0.5rem', backgroundColor: alpha(theme.palette.primary.main, 0.08) }}>
                  {/* No games in this bucket → leave the track empty; a minimum-width
                      fill would otherwise draw a misleading dot for a true 0% share. */}
                  <DurationFillBar pct={pct} color={alpha(theme.palette.primary.main, 0.6)} />
                </Box>
                <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="baseline">
                  <Typography variant="body2" sx={{ fontWeight: 'bold', fontVariantNumeric: 'tabular-nums' }} noWrap>
                    {t('market-analytics:catalogue_tree_games_count', { count: bucket.gamesCount })}
                  </Typography>
                </Stack>
              </Box>
            )
          })}
        </Stack>
      </CardContent>
    </Card>
  )
}
